import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import './credit-note-picker';
import { styles as sharedStyles } from '../styles/shared-styles';
import type { ICreditNote, ICreateSaleTender } from '../api/model/requests';
import { creditNoteRedeemable } from '../datatypes/credit-note';
import {
  money,
  currencyDenominations,
  activeCurrency,
} from '../datatypes/currency';

const EPS = 0.005;

// A tender line being composed. `amountText` is the raw input string and is the
// single source of truth (bound both ways) so the field never fights the
// cursor. Parse to a number only for arithmetic. v1 split contract: at most one
// cash + one card line, any number of credit lines.
interface Line {
  id: number;
  method: 'cash' | 'card' | 'credit';
  amountText: string;
  reference?: string; // credit-note id
  label?: string; // credit-note code (display)
  max?: number; // credit: the note's redeemable cap
}

// P3 - split-payment composer. Opened from the till's "Split payment" button for
// the multi-tender case (the single-method quick path is unchanged). The cashier
// adds Cash / Card / Store-credit lines, each with an amount, until the running
// total covers the bill; cash is the only line that may overpay (change). Credit
// lines are resolved through the existing credit-note-picker. Emits
// `split-confirmed` with the resolved tenders[] (every amount explicit) and
// whether a credit tender is present (so the parent stamps an idempotency key).
@customElement('split-payment-dialog')
export class SplitPaymentDialog extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      sl-dialog::part(panel) {
        max-width: min(440px, 96vw);
      }

      .due {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        padding: 0.5rem 0.7rem;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        margin-bottom: 0.7rem;
      }
      .due .amt {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      .lines {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 0.7rem;
      }
      .line {
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
        padding: 0.5rem 0.6rem;
      }
      .line-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .line-title {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        font-weight: 600;
      }
      .line-title .sub {
        font-weight: 400;
        font-size: 0.78rem;
        color: var(--sl-color-neutral-600);
      }
      .line-body {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-top: 0.45rem;
      }
      .line-body sl-input {
        flex: 1;
      }

      .denoms {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: 0.3rem;
        margin-top: 0.45rem;
      }
      .denoms sl-button::part(base) {
        width: 100%;
        font-variant-numeric: tabular-nums;
        padding: 0 0.2rem;
      }

      .add-row {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        margin-bottom: 0.7rem;
      }

      .summary {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        padding: 0.55rem 0.7rem;
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-50);
        border: 1px solid var(--sl-color-neutral-200);
      }
      .summary .label {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--sl-color-neutral-700);
      }
      .summary .amt {
        font-size: 1.25rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .summary.ok {
        background: var(--sl-color-success-50);
        border-color: var(--sl-color-success-300);
      }
      .summary.ok .amt {
        color: var(--sl-color-success-700);
      }
      .summary.short .amt {
        color: var(--sl-color-warning-700);
      }

      .footer-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    `,
  ];

  @property({ type: Number }) total = 0;
  @property() shopName = '';
  @property({ attribute: false }) memberNotes: ICreditNote[] = [];
  @property() memberName: string | null = null;
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Boolean }) busy = false;
  @property() error: string | null = null;

  @state() private _lines: Line[] = [];
  @state() private _creditPickerOpen = false;
  @state() private _localError: string | null = null;
  private _nextId = 1;

  willUpdate(changed: Map<string, unknown>) {
    // Start each composition fresh when the dialog opens.
    if (changed.has('open') && this.open && !changed.get('open')) {
      this._lines = [];
      this._creditPickerOpen = false;
      this._localError = null;
    }
  }

  private _amt(l: Line): number {
    const n = Number.parseFloat(l.amountText);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
  }
  private get _allocated(): number {
    return (
      Math.round(this._lines.reduce((s, l) => s + this._amt(l), 0) * 100) / 100
    );
  }
  private get _nonCash(): number {
    return (
      Math.round(
        this._lines
          .filter((l) => l.method !== 'cash')
          .reduce((s, l) => s + this._amt(l), 0) * 100
      ) / 100
    );
  }
  private get _remaining(): number {
    const r = Math.round((this.total - this._allocated) * 100) / 100;
    return r > 0 ? r : 0;
  }
  private get _change(): number {
    const c = Math.round((this._allocated - this.total) * 100) / 100;
    return c > 0 ? c : 0;
  }
  private get _hasCash(): boolean {
    return this._lines.some((l) => l.method === 'cash');
  }
  private get _hasCard(): boolean {
    return this._lines.some((l) => l.method === 'card');
  }
  private get _nonCashOverTotal(): boolean {
    return this._nonCash - this.total > EPS;
  }

  private get _canRing(): boolean {
    return (
      this._lines.length > 0 &&
      this._lines.every((l) => this._amt(l) > 0) &&
      this._allocated + EPS >= this.total &&
      !this._nonCashOverTotal
    );
  }

  // Cash is added with preset 0 (starts empty: the cashier enters what was
  // actually received, and any overpay derives as change). Card is added with
  // the remaining balance preset (it pays exactly what's still due).
  private _addLine(method: 'cash' | 'card', preset: number) {
    const v = preset > 0 ? preset : 0;
    this._lines = [
      ...this._lines,
      { id: this._nextId++, method, amountText: v > 0 ? v.toFixed(2) : '' },
    ];
    this._localError = null;
  }

  private _removeLine(id: number) {
    this._lines = this._lines.filter((l) => l.id !== id);
  }

  private _onAmountInput(id: number, raw: string) {
    this._lines = this._lines.map((l) => {
      if (l.id !== id) return l;
      // Clamp a credit line to its note's redeemable balance.
      if (l.method === 'credit' && l.max !== undefined) {
        const n = Number.parseFloat(raw);
        if (Number.isFinite(n) && n > l.max) raw = l.max.toFixed(2);
      }
      return { ...l, amountText: raw };
    });
  }

  private _addDenom(id: number, denom: number) {
    this._lines = this._lines.map((l) =>
      l.id === id ? { ...l, amountText: (this._amt(l) + denom).toFixed(2) } : l
    );
  }

  private _onCreditSelected(e: CustomEvent<{ note: ICreditNote }>) {
    const note = e.detail.note;
    this._creditPickerOpen = false;
    if (this._lines.some((l) => l.reference === note.id)) {
      this._localError = `That credit note (${note.code}) is already on this sale.`;
      return;
    }
    const redeemable = creditNoteRedeemable(note);
    const applied = Math.min(redeemable, this._remaining);
    if (applied <= 0) {
      this._localError = `The sale is already covered - remove a payment before adding more credit.`;
      return;
    }
    this._localError = null;
    this._lines = [
      ...this._lines,
      {
        id: this._nextId++,
        method: 'credit',
        amountText: applied.toFixed(2),
        reference: note.id,
        label: note.code,
        max: redeemable,
      },
    ];
  }

  private _confirm() {
    if (!this._canRing) return;
    const tenders: ICreateSaleTender[] = this._lines.map((l) => ({
      method: l.method,
      amount: this._amt(l),
      reference: l.reference ?? null,
    }));
    const hasCredit = this._lines.some((l) => l.method === 'credit');
    this.dispatchEvent(
      new CustomEvent<{ tenders: ICreateSaleTender[]; hasCredit: boolean }>(
        'split-confirmed',
        {
          detail: { tenders, hasCredit },
          bubbles: true,
          composed: true,
        }
      )
    );
  }

  private _cancel() {
    this.dispatchEvent(
      new CustomEvent('split-cancelled', { bubbles: true, composed: true })
    );
  }

  private _lineLabel(l: Line): string {
    if (l.method === 'cash') return 'Cash';
    if (l.method === 'card') return 'Card';
    return 'Store credit';
  }

  render() {
    const change = this._change;
    const remaining = this._remaining;
    const covered = this._allocated + EPS >= this.total;

    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Split payment"
        ?open=${this.open}
        @sl-request-close=${(e: Event) => {
          e.preventDefault();
          this._cancel();
        }}
      >
        ${
          this.error
            ? html`<sl-alert variant="danger" open style="margin-bottom: 10px;"
                >${this.error}</sl-alert
              >`
            : nothing
        }
        ${
          this._localError
            ? html`<sl-alert variant="warning" open style="margin-bottom: 10px;"
                >${this._localError}</sl-alert
              >`
            : nothing
        }

        <div class="due">
          <span>Total due</span><span class="amt">${money(this.total)}</span>
        </div>

        <div class="lines">
          ${this._lines.map(
            (l) => html`
              <div class="line">
                <div class="line-head">
                  <span class="line-title">
                    <sl-icon
                      name=${l.method === 'cash' ? 'cash' : l.method === 'card' ? 'credit-card' : 'wallet2'}
                    ></sl-icon>
                    ${this._lineLabel(l)}
                    ${l.label ? html`<span class="sub">${l.label}</span>` : nothing}
                  </span>
                  <sl-icon-button
                    name="x-lg"
                    label="Remove payment"
                    @click=${() => this._removeLine(l.id)}
                  ></sl-icon-button>
                </div>
                <div class="line-body">
                  <sl-input
                    type="number"
                    inputmode="decimal"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    .value=${l.amountText}
                    @sl-input=${(e: Event) =>
                      this._onAmountInput(
                        l.id,
                        (e.target as HTMLInputElement).value
                      )}
                  >
                    <span slot="prefix">${activeCurrency().symbol}</span>
                  </sl-input>
                </div>
                ${
                  l.method === 'cash'
                    ? html`<div class="denoms">
                        ${currencyDenominations().map(
                          (d) =>
                            html`<sl-button
                              size="small"
                              @click=${() => this._addDenom(l.id, d)}
                              >+${activeCurrency().symbol}${d}</sl-button
                            >`
                        )}
                      </div>`
                    : nothing
                }
              </div>
            `
          )}
        </div>

        <div class="add-row">
          <sl-button
            size="small"
            ?disabled=${this._hasCash}
            @click=${() => this._addLine('cash', 0)}
          >
            <sl-icon slot="prefix" name="cash"></sl-icon>Cash
          </sl-button>
          <sl-button
            size="small"
            ?disabled=${this._hasCard}
            @click=${() => this._addLine('card', remaining)}
          >
            <sl-icon slot="prefix" name="credit-card"></sl-icon>Card
          </sl-button>
          <sl-button
            size="small"
            @click=${() => (this._creditPickerOpen = true)}
          >
            <sl-icon slot="prefix" name="wallet2"></sl-icon>Store credit
          </sl-button>
        </div>

        ${
          this._nonCashOverTotal
            ? html`<sl-alert variant="danger" open style="margin-bottom: 10px;"
                >Card and store-credit payments can't exceed the total
                (${money(this.total)}); only cash gives change.</sl-alert
              >`
            : nothing
        }

        <div class="summary ${covered ? 'ok' : 'short'}">
          <span class="label"
            >${change > 0 ? 'Change' : covered ? 'Covered' : 'Remaining'}</span
          >
          <span class="amt"
            >${money(change > 0 ? change : covered ? 0 : remaining)}</span
          >
        </div>

        <div slot="footer" class="footer-buttons">
          <sl-button
            variant="text"
            ?disabled=${this.busy}
            @click=${this._cancel}
            >Cancel</sl-button
          >
          <sl-button
            variant="primary"
            ?loading=${this.busy}
            ?disabled=${!this._canRing || this.busy}
            @click=${this._confirm}
          >
            Ring up (${money(this._allocated)})
          </sl-button>
        </div>
      </sl-dialog>

      <credit-note-picker
        .amountDue=${this._remaining}
        .shopName=${this.shopName}
        .memberNotes=${this.memberNotes}
        .memberName=${this.memberName}
        ?open=${this._creditPickerOpen}
        @credit-selected=${this._onCreditSelected}
        @credit-cancelled=${() => (this._creditPickerOpen = false)}
      ></credit-note-picker>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'split-payment-dialog': SplitPaymentDialog;
  }
}
